import { useContext, useEffect, useRef, useState } from 'react';

import { useLazyQuery } from '@apollo/client';

import { LocalizationContext } from 'localization';
import { GET_GOODS_MINIMAL } from 'schemas';
import { EPaymentItemType, TShopGood, TShopItem } from 'types';
import { convertSignCode, convertCodeChar } from 'utils';

import {
  IEntitySearchArgs,
  IReturnEntitySearchValue,
  IGoodsRequest,
  IGoodsResponse,
  ISearchByEanRequest,
  ISearchByEanResponse,
  IServicesRequest,
  IServicesResponse,
  ITariffGridRequest,
  ITariffGridResponse,
  IGoodsMinimalResponse,
} from './interfaces';
import { GOODS, SEARCH_BY_EAN, SERVICES, TARIFF_GRID } from './schema';

export const useEntitySearch = ({
  addItem,
  client_uuid,
  host_group_id,
  shift,
  warehouse,
  selectedGoods,
  useMinimalGoods = false,
}: IEntitySearchArgs): IReturnEntitySearchValue => {
  const { t } = useContext(LocalizationContext);
  const ref = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [searchByEan] = useLazyQuery<ISearchByEanResponse, ISearchByEanRequest>(
    SEARCH_BY_EAN,
    { fetchPolicy: 'no-cache' }
  );
  const [loadServices] = useLazyQuery<IServicesResponse, IServicesRequest>(
    SERVICES,
    { fetchPolicy: 'no-cache' }
  );
  const [loadTariffs] = useLazyQuery<ITariffGridResponse, ITariffGridRequest>(
    TARIFF_GRID,
    { fetchPolicy: 'no-cache' }
  );

  const goodsQuery = useMinimalGoods ? GET_GOODS_MINIMAL : GOODS;
  const [loadGoods] = useLazyQuery<
    IGoodsResponse | IGoodsMinimalResponse,
    IGoodsRequest
  >(goodsQuery, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const { type, parsedStr, gtin } = convertSignCode(value);

    if (type === 'GTIN' || type === 'QR') {
      timeoutRef.current = setTimeout(() => {
        ref.current?.blur();
        setLoading(true);

        searchByEan({
          variables: { ean: gtin as string },
        })
          .then((response) => {
            if (!response.data?.searchByEan) {
              setValue('');
              setError(t('errors.ean.notFound'));
              throw new Error(t('errors.ean.notFound'));
            }

            const { entity_id, entity_type } = response.data.searchByEan;

            if (entity_type === 'TARIFF') {
              return loadTariffs({
                variables: {
                  input: { host_group_id, client_uuid, shift: shift || 1 },
                },
              }).then((tariffsResponse) => {
                const searchedTariff = tariffsResponse.data?.tariffGrid?.find(
                  (tariff) => tariff.id === entity_id
                );
                if (!searchedTariff) {
                  setValue('');
                  setError(t('errors.ean.tariff.add'));
                  throw new Error(t('errors.ean.tariff.add'));
                }
                if (addItem) {
                  return addItem({
                    id: searchedTariff.id,
                    cost: searchedTariff.cost,
                    use_global_discounts: searchedTariff.use_global_discounts,
                    amount: 1,
                    type: EPaymentItemType.TARIFF,
                    original: searchedTariff,
                  });
                }
              });
            }

            if (entity_type === 'SERVICE') {
              return loadServices({
                variables: { input: { client_uuid } },
              }).then((servicesResponse) => {
                const searchedService = servicesResponse.data?.services?.find(
                  (service) => service.id === entity_id
                );
                if (!searchedService) {
                  setValue('');
                  setError(t('errors.ean.service.add'));
                  throw new Error(t('errors.ean.service.add'));
                }
                if (addItem) {
                  return addItem({
                    id: searchedService.id,
                    cost: searchedService.cost,
                    use_global_discounts: searchedService.use_global_discounts,
                    amount: 1,
                    type: EPaymentItemType.SERVICE,
                    original: searchedService,
                  });
                }
              });
            }

            if (entity_type === 'GOOD') {
              return loadGoods({ variables: { input: { client_uuid } } }).then(
                (goodsResponse) => {
                  const goods = goodsResponse.data?.goods;
                  let searchedGood: TShopGood | undefined;

                  if (warehouse) {
                    searchedGood = (goods as TShopGood[])
                      ?.filter(
                        (good) =>
                          !selectedGoods?.some(
                            (selected) => selected.value === good.id
                          )
                      )
                      .find((good) => good.id === entity_id);
                  } else {
                    searchedGood = (goods as TShopGood[])
                      ?.filter((good) => good.amount)
                      .find((good) => good.id === entity_id);
                  }

                  if (!searchedGood) {
                    setValue('');
                    setError(t('errors.ean.good.add'));
                    throw new Error(t('errors.ean.good.add'));
                  }

                  if (addItem) {
                    const itemToAdd: TShopItem = {
                      id: searchedGood.id,
                      cost: searchedGood.cost,
                      use_global_discounts: searchedGood.use_global_discounts,
                      amount: 1,
                      type: EPaymentItemType.GOOD,
                      original: searchedGood,
                      sign:
                        type === 'QR' ? convertCodeChar(parsedStr) : undefined,
                    };

                    return addItem(itemToAdd);
                  }
                }
              );
            }
          })
          .then(() => {
            setValue('');
            ref.current?.focus();
          })
          .catch((err) => {
            console.error(err);
            setError(err.message);
          })
          .finally(() => {
            setLoading(false);
            ref.current?.focus();
          });
      }, 800);
    }
  }, [value, useMinimalGoods]);

  useEffect(() => {
    if (error) {
      setError(undefined);
    }
  }, [value]);

  return {
    value,
    onChange: setValue,
    disabled: loading,
    error,
    ref,
    setError,
  };
};
